import React, { useEffect, useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  TableSortLabel,
  Checkbox,
  FormControlLabel,
  Modal,
  Backdrop,
  Fade,
  IconButton
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Icon from '@material-ui/core/Icon';
import TablePaginationActions from './TablePaginationActions';
import { MetricsTableStyles, SelectedTitle, SelectedTableBody } from './MetricsTableStyles';
import TableFooter from './TableFooter';
import clsx from 'clsx';
import MetricRow from './MetricRow';
import MetricSelectedRow from './MetricSelectedRow';
import Track from '../../../helpers/Analytics';

const MetricsTable = props => {
  const { classes } = props;
  const {
    metrics,
    selectedMetrics,
    onMetricsSelected,
    selectedYears,
    onYearsSelected,
    allYears,
    analyticsPreName,
    disableScrollArrow,
    disableRowClick,
    showSelectedMetrics
  } = props;
  const { onChangePage, onChangeRowsPerPage, onChangeOrder } = props;
  const { totalMetricCount, page, rowsPerPage, order, orderBy } = props;
  const { showPager, enableSorting, showDatapoints, showGeography, showDatapointCount, showDownloadButton, allowDeletingMetrics } = props;
  const [maxToScroll, setMaxToScroll] = useState(0);
  const [timesScrolled, setTimesScrolled] = useState(0);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const metricRows = showSelectedMetrics
    ? metrics
        .filter(m => selectedMetrics.length === 0 || !selectedMetrics.find(s => s.name === m.name))
        .map((metric, i) => (
          <MetricRow
            classes={classes}
            metric={metric}
            selectedMetrics={selectedMetrics}
            onMetricsSelected={onMetricsSelected}
            showGeography={showGeography}
            allowDeletingMetrics={allowDeletingMetrics}
            showDatapointCount={showDatapointCount}
            allYears={allYears}
            showDatapoints={showDatapoints}
            key={i}
            analyticsPreName={analyticsPreName}
            disableRowClick={disableRowClick}
            openModal={() => handleOpen()}
          />
        ))
    : metrics.map((metric, i) => (
        <MetricRow
          classes={classes}
          metric={metric}
          selectedMetrics={selectedMetrics}
          onMetricsSelected={onMetricsSelected}
          showGeography={showGeography}
          allowDeletingMetrics={allowDeletingMetrics}
          showDatapointCount={showDatapointCount}
          allYears={allYears}
          showDatapoints={showDatapoints}
          key={i}
          analyticsPreName={analyticsPreName}
          disableRowClick={disableRowClick}
          openModal={() => handleOpen()}
        />
      ));

  const handleOpen = () => {
    setModalIsOpen(true);
  };

  let sortedMetrics = showSelectedMetrics ? [...selectedMetrics].sort((a, b) => {
    if (order === 'asc') {
      return a.name.localeCompare(b.name);
    } else {
      return b.name.localeCompare(a.name);
    }
  }) : [];

  const handleYearSelected = year => event => {
    const newSelectedYears = selectedYears === null ? allYears : selectedYears;
    onYearsSelected(
      event.target.checked ? [...new Set([...newSelectedYears, year])].sort((y1, y2) => y1 - y2) : newSelectedYears.filter(y => y !== year)
    );
    Track('Metric', { id: 'Year-Toggle', metrics: metrics.map(m => m.name).join(', '), year, checked: event.target.checked });
  };

  const handleSortRequest = property => {
    const newOrderBy = property;
    let newOrder = 'desc';

    if (orderBy === property && order === 'desc') {
      newOrder = 'asc';
    }

    onChangeOrder(newOrder, newOrderBy);
  };

  useEffect(() => {
    if (showGeography) {
      const table = document.getElementById('ChartingMetricsTable');
      table.scrollLeft = 9999;
      setMaxToScroll(Math.ceil(document.getElementById('ChartingMetricsTable').scrollLeft / 116));
      setTimesScrolled(Math.ceil(document.getElementById('ChartingMetricsTable').scrollLeft / 116));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateScrollButtons = direction => {
    if (direction === 0 && timesScrolled < maxToScroll) {
      // right
      setTimesScrolled(timesScrolled + 1);
      document.getElementById('ChartingMetricsTable').scrollLeft += 116;
    } else if (direction === 1 && timesScrolled && timesScrolled > 0) {
      // left
      setTimesScrolled(timesScrolled - 1);
      document.getElementById('ChartingMetricsTable').scrollLeft -= 116;
    }
  };

  return (
    <>
      <div
        id='ChartingMetricsTable'
        className={clsx(classes.MetricsTable_tableWrapper, { [classes.MetricsTable_chartedTableWrapper]: showGeography })}
      >
        {showSelectedMetrics && selectedMetrics.length > 0 && (
          <div>
            <SelectedTitle>Selections</SelectedTitle>
            <SelectedTableBody>
              {sortedMetrics.map((metric, i) => (
                <MetricSelectedRow
                  classes={classes}
                  metric={metric}
                  selectedMetrics={selectedMetrics}
                  onMetricsSelected={onMetricsSelected}
                  showGeography={showGeography}
                  allowDeletingMetrics={allowDeletingMetrics}
                  showDatapointCount={showDatapointCount}
                  allYears={allYears}
                  showDatapoints={showDatapoints}
                  key={i}
                  analyticsPreName={analyticsPreName}
                  disableRowClick={disableRowClick}
                />
              ))}
            </SelectedTableBody>
          </div>
        )}
        <Table
          className={clsx(classes.MetricsTable_table, {
            [classes.MetricsTable_chartedTable]: showGeography,
            [classes.MetricsTable_hover]: !showGeography && metrics && metrics.length > 0
          })}
          size='small'
        >
          <TableHead>
            <TableRow className={clsx(classes.MetricsTable_tableHead, { [classes.MetricsTable_chartedTableHead]: showGeography })}>
              <TableCell className={clsx(classes.MetricsTable_chartIcon, { [classes.MetricsTable_chartedIcon]: showGeography })}>
                {showDownloadButton ? null : <Icon className='fas fa-chart-bar' style={{ color: '#747474' }} />}
              </TableCell>
              <TableCell
                className={clsx(classes.MetricsTable_MetricNameHead, { [classes.MetricsTable_ChartedMetricNameHead]: showGeography })}
                key='name'
                sortDirection={orderBy === 'name' ? order : false}
              >
                {enableSorting ? (
                  <Tooltip title='Sort' placement='bottom-start' enterDelay={300}>
                    <TableSortLabel
                      className={classes.MetricsTable_TableSort}
                      active={orderBy === 'name'}
                      direction={order}
                      onClick={() => handleSortRequest('name')}
                    >
                      Name
                    </TableSortLabel>
                  </Tooltip>
                ) : (
                  'Name'
                )}
              </TableCell>
              {showGeography && (
                <>
                  <TableCell className={clsx(classes.MetricsTable_MetricGeoHead, { [classes.MetricsTable_ChartedMetricGeoHead]: showGeography })}>
                    Geo Select
                  </TableCell>
                </>
              )}
              {showDatapointCount && (
                <TableCell key='datapointCount' sortDirection={orderBy === 'datapointCount' ? order : false}>
                  {enableSorting ? (
                    <Tooltip title='Sort' placement='bottom-start' enterDelay={300}>
                      <TableSortLabel active={orderBy === 'datapointCount'} direction={order} onClick={() => handleSortRequest('datapointCount')}>
                        Datapoint Count
                      </TableSortLabel>
                    </Tooltip>
                  ) : (
                    'Datapoint Count'
                  )}
                </TableCell>
              )}
              {metrics &&
                showDatapoints &&
                allYears.map(year => {
                  const checked = selectedYears === null || selectedYears.includes(year);
                  return (
                    <TableCell className={classes.MetricsTable_yearCells} key={year}>
                      <Tooltip title={`${checked ? 'Remove year from' : 'Add year to'} chart`}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checked}
                              icon={<span className={classes.icon} />}
                              checkedIcon={<span className={clsx(classes.icon, classes.checkedIcon)} />}
                              onChange={handleYearSelected(year)}
                            />
                          }
                          label={year}
                        />
                      </Tooltip>
                    </TableCell>
                  );
                })}
            </TableRow>
            {!disableScrollArrow && (
              <Icon
                id='ScrollRight'
                className='fas fa-chevron-circle-right'
                onClick={() => updateScrollButtons(0)}
                style={{
                  position: 'absolute',
                  cursor: timesScrolled === maxToScroll ? 'default' : 'pointer',
                  right: 10,
                  top: 68,
                  backgroundColor: 'white',
                  borderRadius: 20,
                  color: timesScrolled === maxToScroll ? 'gray' : 'black'
                }}
              >
                RIGHT
              </Icon>
            )}

            {!disableScrollArrow && (
              <Icon
                id='ScrollLeft'
                className='fas fa-chevron-circle-left'
                onClick={() => updateScrollButtons(1)}
                style={{
                  position: 'absolute',
                  cursor: timesScrolled === 0 ? 'default' : 'pointer',
                  top: 68,
                  marginLeft: 10,
                  backgroundColor: 'white',
                  borderRadius: 20,
                  color: timesScrolled === 0 ? 'gray' : 'black'
                }}
              >
                LEFT
              </Icon>
            )}
          </TableHead>
          {metrics && (
            <TableBody className={classes.MetricsTable_body}>
              {(metrics.length === 0 && (
                <TableRow>
                  <TableCell className={classes.MetricsTable_NoResultCells} />
                  <TableCell className={classes.MetricsTable_NoResultCells}>
                    <div>No Results</div>
                  </TableCell>
                </TableRow>
              )) ||
                metricRows}
            </TableBody>
          )}
        </Table>
      </div>
      {showPager && metrics && metrics.length > 0 ? (
        <TablePagination
          className={classes.MetricsTable_PageOptions}
          component='div'
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          count={totalMetricCount}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{
            inputProps: { 'aria-label': 'rows per page', 'data-analytics': 'buildyourforecast-rowsperpg' },
            native: true
          }}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          classes={{ spacer: classes.MetricsTable_PageOptionsSpacer, caption: classes.MetricsTable_PageOptionsCaption }}
        />
      ) : null}
      {!showPager && (
        <TableFooter
          classes={classes}
          showDownloadButton={showDownloadButton}
          metrics={metrics}
          selectedMetrics={selectedMetrics}
          allYears={allYears}
        />
      )}
      <Modal
        open={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={modalIsOpen}>
          <div className={classes.modalWrapper}>
            <IconButton className={classes.modalClose} edge='end' color='inherit' onClick={() => setModalIsOpen(false)} aria-label='close'>
              <CloseIcon />
            </IconButton>
            <span className={classes.modalTextBold}>Max Metrics Limit Reached</span>
            <span className={classes.modalText}>
              You've selected 10 metrics, which is the maximum allowed. Please remove one or more metrics to add new ones.
            </span>
          </div>
        </Fade>
      </Modal>
    </>
  );
};

export default withStyles(MetricsTableStyles)(MetricsTable);
